/**
 * Who may create, change and delete the contacts on a client.
 *
 * ⚠️ CONTACTS WERE NOT GATED AT ALL.
 *
 * Contacts live in their own table and have their own management page, but none of its actions
 * asked for a permission — so every role could delete one, including view_only, which cannot
 * even edit a tender. Raised against the BROKER role: a broker is meant to work a tender, not
 * remove a client's contact records.
 *
 * Three keys rather than one, mirroring client.create / client.edit / client.delete, so a role
 * can be allowed to add and correct contacts while still being refused the destructive action.
 * Deleting is the one that cannot be undone from the UI, which is why it is separable.
 *
 * ⚠️ UI GATING ONLY, like every other key in this system. It hides the buttons; it does not stop
 * a crafted call to the contact table. Row Level Security is what guards the data itself.
 */

export const CONTACT_CREATE_PERMISSION = 'contact.create';
export const CONTACT_EDIT_PERMISSION = 'contact.edit';
export const CONTACT_DELETE_PERMISSION = 'contact.delete';

/** Every contact key, for the server's key list and the admin tab. */
export const CONTACT_PERMISSION_KEYS = [
  CONTACT_CREATE_PERMISSION,
  CONTACT_EDIT_PERMISSION,
  CONTACT_DELETE_PERMISSION,
] as const;

type Can = (permission: string) => boolean;

/** May this role add a contact? Admins always may — can() short-circuits for them. */
export const canCreateContacts = (can: Can): boolean => can(CONTACT_CREATE_PERMISSION);

/** May this role change an existing contact? */
export const canEditContacts = (can: Can): boolean => can(CONTACT_EDIT_PERMISSION);

/**
 * May this role delete a contact?
 *
 * Deliberately independent of the other two: the BROKER role is granted create and edit so it can
 * keep a client's details current, and refused this one.
 */
export const canDeleteContacts = (can: Can): boolean => can(CONTACT_DELETE_PERMISSION);
